/**
 * Stale-server-action detection.
 *
 * When the app container is redeployed, Next.js bakes new server-action
 * hashes into the bundle. Browsers holding a cached page from before the
 * deploy POST the OLD hashes and the server can't find them — Next.js
 * throws `Failed to find Server Action "<hash>". This request might be
 * from an older or newer deployment.`
 *
 * The error originates inside Next.js's action dispatcher BEFORE any
 * downstream code (auth, AI service, Airtable) is reached, so it surfaces
 * to the user as a generic "something went wrong" toast. This module
 * provides a pure matcher used by the global handler in
 * <StaleActionReloader/> to detect those errors and prompt a soft reload.
 *
 * The matcher is intentionally substring-based (not hash-equality) so that
 * future Next.js wording tweaks don't silently regress us. The substring
 * "Failed to find Server Action" has been stable across Next.js 13/14/15
 * (verified against the linked docs page at the time of writing).
 */

const NEEDLE = "Failed to find Server Action";

/**
 * Return true when the supplied error/event payload looks like a stale-
 * server-action mismatch. Accepts:
 *   - an Error                       (most common path — thrown from the
 *                                     dispatcher and bubbled up)
 *   - a string                       (some Next.js paths reject with a string)
 *   - PromiseRejectionEvent.reason   (window 'unhandledrejection' listener)
 *   - ErrorEvent / ErrorEvent.error  (window 'error' listener)
 *   - an unknown                     (defensively, so callers can pass raw
 *                                     event values without narrowing first)
 *
 * Returns false for null / undefined / non-matching payloads.
 */
export function isStaleServerActionError(payload: unknown): boolean {
  if (payload == null) return false;

  if (typeof payload === "string") {
    return payload.includes(NEEDLE);
  }

  if (payload instanceof Error) {
    return (
      (typeof payload.message === "string" && payload.message.includes(NEEDLE)) ||
      (typeof payload.stack === "string" && payload.stack.includes(NEEDLE))
    );
  }

  // Plain object with a message field (e.g. some rejection reasons).
  if (typeof payload === "object") {
    const obj = payload as { message?: unknown; reason?: unknown; error?: unknown };

    if (typeof obj.message === "string" && obj.message.includes(NEEDLE)) {
      return true;
    }

    // PromiseRejectionEvent — recurse into .reason
    if ("reason" in obj && obj.reason !== payload) {
      if (isStaleServerActionError(obj.reason)) return true;
    }

    // ErrorEvent — recurse into .error
    if ("error" in obj && obj.error !== payload) {
      if (isStaleServerActionError(obj.error)) return true;
    }
  }

  return false;
}
